import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { fetchAiReviews } from "api/aiReviewService";
import { AIReview } from "common/models/types";
import { COLOR_SYSTEM, Text } from "components/atoms";

/**
 * 보고서별 결제방식 라벨.
 * - premiumRole="유료프리미엄" → 구독으로 무료
 * - premiumRole=면허(약사회 등) → 해당 단체 지원으로 무료
 * - premiumRole=null → 포인트 차감(1회 200P)
 */
const getUsageLabel = (r: AIReview): { text: string; bg: string; fg: string } => {
  if (r.premiumRole === "유료프리미엄") {
    return {
      text: "구독 무료",
      bg: COLOR_SYSTEM.get("Blue")[10],
      fg: COLOR_SYSTEM.get("Blue")[600],
    };
  }
  if (r.premiumRole) {
    return {
      text: `${r.premiumRole} 지원`,
      bg: COLOR_SYSTEM.get("Green")[10],
      fg: COLOR_SYSTEM.get("Green")[600],
    };
  }
  return {
    text: "포인트 200P",
    bg: COLOR_SYSTEM.get("Gray")[100],
    fg: COLOR_SYSTEM.get("Gray")[700],
  };
};

const formatDate = (r: AIReview): string => {
  const d = r.reviewDate ?? r.createdAt;
  return d ? new Date(d).toLocaleDateString("ko-KR") : "";
};

const ReportUsagePage: React.FC = () => {
  const [reviews, setReviews] = useState<AIReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetchAiReviews({});
        if (mounted) setReviews(res.data || []);
      } catch {
        if (mounted) setReviews([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Container>
      <Desc textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
        각 보고서가 어떤 방식으로 처리됐는지 확인할 수 있어요.
      </Desc>

      {loading ? (
        <Empty>불러오는 중…</Empty>
      ) : reviews.length === 0 ? (
        <Empty>보고서 사용 내역이 없습니다.</Empty>
      ) : (
        <List>
          {reviews.map((r) => {
            const label = getUsageLabel(r);
            return (
              <Row key={r.id}>
                <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")[900]}>
                  보고서 #{r.id}
                </Text>
                <DateText textType="Body2" color={COLOR_SYSTEM.get("Gray")[500]}>
                  {formatDate(r)}
                </DateText>
                <Label style={{ background: label.bg, color: label.fg }}>{label.text}</Label>
              </Row>
            );
          })}
        </List>
      )}
    </Container>
  );
};

export default ReportUsagePage;

const Container = styled.div`
  width: 100%;
`;

const Desc = styled(Text)`
  display: block;
  margin-bottom: 24px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[200]};
  border-radius: 10px;
`;

const DateText = styled(Text)`
  flex: 1;
`;

const Label = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const Empty = styled.div`
  padding: 60px 0;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
`;
